import { useEffect } from 'react'
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
  Routes,
  useLocation
} from 'react-router-dom'

import { ROUTES } from '@zing/shared/constants'
import { InnerPage } from '@zing/active-tenant/main/pages/defaults/InnerPage'
import { HotsiteInnerPage } from '@zing/active-tenant/main/pages/defaults/HotsiteInnerPage'
import { AuthRoute } from '@zing/shared/auth/AuthRoute'
import { initializeGA, logPageView } from '@zing/shared/components/GoogleAnalytics/GAInitializer'
import { initializeClarity } from '@zing/shared/components/Clarity/Clarity'
import { useServerFallbackGuard } from '@zing/shared/smartNav/useServerFallbackGuard'

import { hubRoutes } from './hub'
import ScrollToTop from './ScrollToTop'
import Page from './Page'
import HotsitePage from './HotsitePage'
import { PageSourceBadge } from '../PageSourceBadge'

/**
 * Data-router shell so hooks like `useBlocker` work (e.g. unsaved user-config prompt).
 * Nested `<Routes>` keep the previous BrowserRouter route tree intact.
 */
export const RoutesBuilder = (): JSX.Element => {
  const location = useLocation()

  // Server-env only: redirect direct/bookmarked/hard-reloaded unmigrated routes to
  // the legacy host (the Vite middleware that does this in local dev doesn't exist there).
  useServerFallbackGuard()

  useEffect(() => {
    initializeGA()
    initializeClarity()
  }, [])

  useEffect(() => {
    logPageView()
  }, [location])

  return (
    <>
    
    {import.meta.env.VITE_ENV !== 'prod' && <PageSourceBadge />}
    <ScrollToTop>
      <Routes>
          <Route path={ROUTES.NOT_FOUND} element={Page(ROUTES.NOT_FOUND)} />
          <Route path="/not-found" element={Page(ROUTES.NOT_FOUND)} />

          {/* Hotsite */}
          <Route path={ROUTES.HOTSITE.ROOT} element={<HotsiteInnerPage />}>
            <Route path={ROUTES.HOTSITE.ROOT} element={HotsitePage(ROUTES.HOTSITE.ROOT)} />
            <Route path={ROUTES.HOTSITE.MARKETPLACE} element={HotsitePage(ROUTES.HOTSITE.MARKETPLACE)} />
            <Route path={ROUTES.HOTSITE.EVENTS_PAGE} element={HotsitePage(ROUTES.HOTSITE.EVENTS_PAGE)} />
            <Route path={ROUTES.HOTSITE.CHALLENGE} element={HotsitePage(ROUTES.HOTSITE.CHALLENGE)} />
            <Route path={ROUTES.HOTSITE.EVENT} element={HotsitePage(ROUTES.HOTSITE.EVENT)} />
            <Route path={ROUTES.HOTSITE.REGISTER.EVALUATOR} element={HotsitePage(ROUTES.HOTSITE.REGISTER.EVALUATOR)} />
            <Route path={ROUTES.HOTSITE.REGISTER.RESOLVER} element={HotsitePage(ROUTES.HOTSITE.REGISTER.RESOLVER)} />
            <Route path={ROUTES.HOTSITE.REGISTER.SIMPLIFIED_RESOLVER} element={HotsitePage(ROUTES.HOTSITE.REGISTER.SIMPLIFIED_RESOLVER)} />
            <Route path={ROUTES.HOTSITE.REGISTER.RESOLVER_CONNECTION} element={HotsitePage(ROUTES.HOTSITE.REGISTER.RESOLVER_CONNECTION, ROUTES.HOTSITE.REGISTER.RESOLVER)} />
            <Route path={ROUTES.HOTSITE.REGISTER.PUBLIC_ORGAN_MEMBER} element={HotsitePage(ROUTES.HOTSITE.REGISTER.PUBLIC_ORGAN_MEMBER)} />
            <Route path={ROUTES.HOTSITE.REGISTER.PUBLIC_ADMINISTRATION} element={HotsitePage(ROUTES.HOTSITE.REGISTER.PUBLIC_ADMINISTRATION)} />
            <Route path={ROUTES.HOTSITE.REGISTER.NATURAL_PERSON} element={HotsitePage(ROUTES.HOTSITE.REGISTER.NATURAL_PERSON)} />
            <Route path={ROUTES.HOTSITE.REGISTER.CAIXA_EMPLOYEE} element={HotsitePage(ROUTES.HOTSITE.REGISTER.CAIXA_EMPLOYEE)} />
            <Route path={ROUTES.HOTSITE.CONTACT} element={HotsitePage(ROUTES.HOTSITE.CONTACT)} />
            <Route path={ROUTES.HOTSITE.RESERVATION} element={HotsitePage(ROUTES.HOTSITE.RESERVATION)} />
            <Route path={ROUTES.HOTSITE.MEETING_ROOM_RESERVATION} element={HotsitePage(ROUTES.HOTSITE.MEETING_ROOM_RESERVATION)} />

            {/* TODO: Está correto o domínio "/mapadainovacao", exibir a página de erro 404? */}
            <Route path={ROUTES.HOTSITE.INNOVATION_MAP} element={HotsitePage(ROUTES.HOTSITE.INNOVATION_MAP, ROUTES.NOT_FOUND)} />
          </Route>

          {/* User */}
          <Route path={ROUTES.USER_AUTH.LOGIN} element={Page(ROUTES.USER_AUTH.LOGIN)} />
          <Route path={ROUTES.USER_AUTH.FORGOT_PASSWORD} element={Page(ROUTES.USER_AUTH.FORGOT_PASSWORD)} />
          <Route path={ROUTES.USER_AUTH.RESET_PASSWORD} element={Page(ROUTES.USER_AUTH.RESET_PASSWORD)} />

          {/* HUB */}
          {/* Hub sub-paths (e.g. /dashboard) are relative to the parent /hub.
              Strip the leading / so React Router v6 combines them correctly:
              parent "/hub" + child "dashboard" → matches "/hub/dashboard". */}
          <Route path={ROUTES.HUB.ROOT} element={<InnerPage />}>
            {hubRoutes.map((route, index) => (
              <Route
                key={index}
                path={route.path.replace(/^\//, '')}
                element={<AuthRoute features={route.features} element={route.component} />}
              />
            ))}
          </Route>
      </Routes>
    </ScrollToTop>
    </>
  )
}

const shellRouter = createBrowserRouter(
  createRoutesFromElements(<Route path="*" element={<RoutesBuilder />} />)
)

export const CreateRoutes = (): JSX.Element => {
  return <RouterProvider router={shellRouter} />
}
